interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

class SimpleCache {
  private cache = new Map<string, CacheEntry<any>>();
  private defaultTTL = 30 * 60 * 1000; // 30 minutes in milliseconds
  private storagePrefix = 'ecs-scheduled-tasks-cache:';

  constructor() {
    // Load existing cache from localStorage on initialization
    this.loadFromLocalStorage();
  }

  private loadFromLocalStorage(): void {
    try {
      const keys = Object.keys(localStorage);
      for (const storageKey of keys) {
        if (storageKey.startsWith(this.storagePrefix)) {
          const cacheKey = storageKey.substring(this.storagePrefix.length);
          const stored = localStorage.getItem(storageKey);
          if (stored) {
            const entry = JSON.parse(stored) as CacheEntry<any>;
            // Only load if not expired
            if (Date.now() <= entry.expiresAt) {
              this.cache.set(cacheKey, entry);
            } else {
              // Clean up expired entries from localStorage
              localStorage.removeItem(storageKey);
            }
          }
        }
      }
    } catch (e) {
      // Ignore localStorage errors (e.g., disabled, quota exceeded)
      // eslint-disable-next-line no-console
      console.warn('Failed to load cache from localStorage:', e);
    }
  }

  set<T>(key: string, data: T): void {
    const now = Date.now();
    const entry: CacheEntry<T> = {
      data,
      timestamp: now,
      expiresAt: now + this.defaultTTL,
    };

    // Store in memory
    this.cache.set(key, entry);

    // Store in localStorage
    try {
      localStorage.setItem(
        `${this.storagePrefix}${key}`,
        JSON.stringify(entry)
      );
    } catch (e) {
      // Ignore localStorage errors, fall back to memory-only
      // eslint-disable-next-line no-console
      console.warn('Failed to save to localStorage:', e);
    }
  }

  get<T>(key: string): T | null {
    // Try memory first
    let entry = this.cache.get(key);

    // If not in memory, try localStorage
    if (!entry) {
      try {
        const stored = localStorage.getItem(`${this.storagePrefix}${key}`);
        if (stored) {
          entry = JSON.parse(stored) as CacheEntry<T>;
          // Restore to memory cache
          this.cache.set(key, entry);
        }
      } catch (e) {
        // Ignore localStorage errors
        // eslint-disable-next-line no-console
        console.warn('Failed to read from localStorage:', e);
      }
    }

    if (!entry) return null;

    // Check expiration
    if (Date.now() > entry.expiresAt) {
      this.delete(key);
      return null;
    }

    return entry.data as T;
  }

  getWithTimestamp<T>(key: string): { data: T; timestamp: number } | null {
    // Try memory first
    let entry = this.cache.get(key);

    // If not in memory, try localStorage
    if (!entry) {
      try {
        const stored = localStorage.getItem(`${this.storagePrefix}${key}`);
        if (stored) {
          entry = JSON.parse(stored) as CacheEntry<T>;
          // Restore to memory cache
          this.cache.set(key, entry);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('Failed to read from localStorage:', e);
      }
    }

    if (!entry) return null;

    // Check expiration
    if (Date.now() > entry.expiresAt) {
      this.delete(key);
      return null;
    }

    return { data: entry.data as T, timestamp: entry.timestamp };
  }

  delete(key: string): boolean {
    // Delete from memory
    const deleted = this.cache.delete(key);

    // Delete from localStorage
    try {
      localStorage.removeItem(`${this.storagePrefix}${key}`);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Failed to delete from localStorage:', e);
    }

    return deleted;
  }

  clear(): void {
    // Clear memory
    this.cache.clear();

    // Clear localStorage entries
    try {
      const keys = Object.keys(localStorage);
      for (const key of keys) {
        if (key.startsWith(this.storagePrefix)) {
          localStorage.removeItem(key);
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Failed to clear localStorage:', e);
    }
  }

  getKeysMatching(pattern: RegExp): string[] {
    return Array.from(this.cache.keys()).filter(key => pattern.test(key));
  }

  // Clean up expired entries from both memory and localStorage
  cleanup(): void {
    const now = Date.now();

    // Clean up memory cache
    for (const [key, entry] of this.cache.entries()) {
      if (now > entry.expiresAt) {
        this.delete(key); // Use delete() to also remove from localStorage
      }
    }

    // Clean up localStorage (in case there are entries not in memory)
    try {
      const keys = Object.keys(localStorage);
      for (const storageKey of keys) {
        if (storageKey.startsWith(this.storagePrefix)) {
          const stored = localStorage.getItem(storageKey);
          if (stored) {
            const entry = JSON.parse(stored) as CacheEntry<any>;
            if (now > entry.expiresAt) {
              localStorage.removeItem(storageKey);
            }
          }
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Failed to cleanup localStorage:', e);
    }
  }
}

export const apiCache = new SimpleCache();

// Utility function to create cache keys
export const createCacheKey = (
  prefix: string,
  params: Record<string, any>,
): string => {
  const sortedParams = Object.keys(params)
    .sort()
    .reduce(
      (result, key) => {
        result[key] = params[key];
        return result;
      },
      {} as Record<string, any>,
    );

  return `${prefix}:${JSON.stringify(sortedParams)}`;
};
